import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { catchError, firstValueFrom, throwError } from "rxjs";
import {
  CoreComparisonDto,
  DesignerApplyRequest,
  DesignerLayoutDto
} from "@app/services/api/v1/visitDesigner/dto/designer-dto";

/**
 * Thin HTTP wrapper for the Visit Designer endpoints. Each method returns a Promise so callers can
 * `await` it in component code without managing subscriptions, matching the convention used by
 * `VisitRequirementsService`.
 */
@Injectable({ providedIn: "root" })
export class VisitDesignerService {
  private http = inject(HttpClient);
  private clientConfigService = inject(ClientConfigService);

  /** GET /scheduler/api/v1/visitDesigner/jobs/{jobId}/layout */
  public async getLayout(jobId: number): Promise<DesignerLayoutDto> {
    const url = `${this.baseUrl}/jobs/${jobId}/layout`;
    return firstValueFrom(
      this.http
        .get<DesignerLayoutDto>(url, { withCredentials: true })
        .pipe(catchError((e) => this.fail(`get layout for job ${jobId}`, e)))
    );
  }

  /** POST /scheduler/api/v1/visitDesigner/jobs/{jobId}/apply */
  public async apply(
    request: DesignerApplyRequest
  ): Promise<DesignerLayoutDto> {
    const url = `${this.baseUrl}/jobs/${request.jobId}/apply`;
    return firstValueFrom(
      this.http
        .post<DesignerLayoutDto>(url, request, { withCredentials: true })
        .pipe(
          catchError((e) =>
            this.fail(`apply layout for job ${request.jobId}`, e)
          )
        )
    );
  }

  /** GET /scheduler/api/v1/visitDesigner/jobs/{jobId}/coreComparison */
  public async getCoreComparison(jobId: number): Promise<CoreComparisonDto> {
    const url = `${this.baseUrl}/jobs/${jobId}/coreComparison`;
    return firstValueFrom(
      this.http
        .get<CoreComparisonDto>(url, { withCredentials: true })
        .pipe(
          catchError((e) =>
            this.fail(`get core comparison for job ${jobId}`, e)
          )
        )
    );
  }

  private get baseUrl(): string {
    return `${this.clientConfigService.config.serviceUrl}/api/v1/visitDesigner`;
  }

  private fail(action: string, error: unknown) {
    console.error(`VisitDesignerService: failed to ${action}:`, error);
    return throwError(() => error);
  }
}
